import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Save } from "lucide-react";
import { toast } from "sonner";

const FREQ = [["daily", "Ogni giorno"], ["weekly", "Ogni settimana"], ["biweekly", "Ogni 2 settimane"], ["monthly", "Ogni mese"]];
const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const pad = (n) => String(n).padStart(2, "0");
const fmtNext = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const JobRow = ({ j, onChange }) => {
  const set = (k, v) => onChange({ ...j, [k]: v });
  const user = j.frequency === "user";
  return (
    <div className={`rounded-xl border p-3 space-y-3 ${j.enabled ? "bg-card" : "bg-secondary/40"}`} data-testid={`email-job-${j.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-sm">{j.label}</div>
          <div className="text-xs text-muted-foreground" data-testid={`email-job-next-${j.id}`}>
            {j.enabled ? `Prossimo invio: ${fmtNext(j.next_run)}` : "Disattivata"}
          </div>
        </div>
        <Switch checked={j.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid={`email-job-enabled-${j.id}`} />
      </div>
      {j.enabled && !user && (
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={j.frequency} onValueChange={(v) => set("frequency", v)}>
            <SelectTrigger className="w-44 h-9" data-testid={`email-job-freq-${j.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{FREQ.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {(j.frequency === "weekly" || j.frequency === "biweekly") && (
            <Select value={String(j.day_of_week)} onValueChange={(v) => set("day_of_week", Number(v))}>
              <SelectTrigger className="w-36 h-9" data-testid={`email-job-dow-${j.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {j.frequency === "monthly" && (
            <div className="flex items-center gap-1 text-sm">giorno
              <Input type="number" min="1" max="28" value={j.day_of_month} onChange={(e) => set("day_of_month", Number(e.target.value))} className="w-16 h-9" data-testid={`email-job-dom-${j.id}`} />
            </div>
          )}
          <div className="flex items-center gap-1 text-sm" data-testid={`email-job-time-${j.id}`}>alle
            <Select value={String(j.hour)} onValueChange={(v) => set("hour", Number(v))}>
              <SelectTrigger className="w-20 h-9" data-testid={`email-job-hour-${j.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{[...Array(24)].map((_, h) => <SelectItem key={h} value={String(h)}>{pad(h)}</SelectItem>)}</SelectContent>
            </Select>:
            <Select value={String(j.minute)} onValueChange={(v) => set("minute", Number(v))}>
              <SelectTrigger className="w-20 h-9" data-testid={`email-job-minute-${j.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{[...new Set([0, 15, 30, 45, j.minute])].sort((x, y) => x - y).map((m) => <SelectItem key={m} value={String(m)}>{pad(m)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}
      {j.enabled && user && <p className="text-xs text-muted-foreground">Ogni utente sceglie giorno e ora dal proprio profilo: qui puoi solo attivarla o disattivarla.</p>}
    </div>
  );
};

export const EmailScheduleAdmin = () => {
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/email-schedule").then((r) => setJobs(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { jobs: Object.fromEntries(jobs.map(({ id, enabled, frequency, day_of_week, day_of_month, hour, minute }) =>
        [id, { enabled, frequency, day_of_week, day_of_month, hour, minute }])) };
      const { data } = await api.put("/admin/email-schedule", body);
      setJobs(data);
      toast.success("Frequenza delle email aggiornata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-2xl" data-testid="email-schedule-admin">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
        <CardTitle className="font-heading text-lg flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /> Frequenza invio email</CardTitle>
        <Button onClick={save} disabled={saving} data-testid="email-schedule-save"><Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio…" : "Salva"}</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">Orari in ora italiana. Le modifiche sono applicate subito.</p>
        <div className="grid md:grid-cols-2 gap-2">
          {jobs.map((j) => <JobRow key={j.id} j={j} onChange={(nj) => setJobs((all) => all.map((x) => (x.id === nj.id ? nj : x)))} />)}
        </div>
      </CardContent>
    </Card>
  );
};
